import { useEffect, useRef, useState } from "react";
import { Pencil, X } from "lucide-react";
import toast from "react-hot-toast";
import { validateImageFile, ErrorMessage } from "../../utlis/function";
import { callApi } from "../../services/ApiServices";
import { useGlobalStates } from "../../utlis/ClubsContext";
import { UserRole } from "../../utlis/constant";
import CommonLoader from "../common/CommonLoader";

// Each slot holds a single image. `field` is the key sent to
// upload-website-image. Slots marked optional are only rendered on the club
// page when an image has been uploaded for them.
const IMAGE_SLOTS = [
  {
    key: "hero",
    field: "heroImage",
    title: "Hero Photo",
    section: "Card Type / Hero Image",
    description:
      "Club interior with DRS screens visible. Falls back to Workout Anytime brand photography when no club-specific photo exists.",
    optional: false,
  },
  {
    key: "slot2",
    field: "screenPlace",
    title: "Photo Slot 2 · Screens in Place",
    description:
      "A wide shot of the screens mounted in the club. If no photo is uploaded for this club, this band is not rendered at all and the sections above and below sit flush together.",
    optional: true,
  },
  {
    key: "slot3",
    field: "clubFloor",
    title: "Photo Slot 3 · Club Floor",
    section: "What you're buying",
    description:
      "Members using the club. Sits alongside this section when available; the column simply ends after the text when it isn't.",
    optional: true,
  },
  {
    key: "slot4",
    field: "memberFloor",
    title: "Photo Slot 4 · Members on the Floor",
    description:
      "People actually using the club. Makes the traffic numbers above feel observed rather than claimed. Omitted entirely when no photo exists.",
    optional: true,
  },
  {
    key: "slot5",
    field: "screenCloseUp",
    title: "Photo Slot 5 · Screen Close-up",
    description:
      "A single screen shot close, showing what an ad actually looks like in place. Strongest final image before the closing call to action, and the one most buyers want to see. Omitted when empty.",
    optional: true,
  },
];

// Normalize an image from get-website-image into { id, fileUrl, url }.
const toImage = (value) => {
  if (!value) return null;
  if (typeof value === "string") return { id: value, fileUrl: value, url: value };
  const fileUrl = value.fileUrl || value.url || value.path;
  if (!value.id || !fileUrl) return null;
  return { id: value.id, fileUrl, url: fileUrl };
};

const ImageSlotCard = ({ slot, isViewer, initialImage }) => {
  const { selectedClub } = useGlobalStates();
  // `saved` is the last saved image, `draft` is what the user currently sees.
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isBusy = isUploading || isSaving;
  const inputRef = useRef(null);
  const objectUrls = useRef([]);

  // Load the image already saved for this slot
  useEffect(() => {
    setSaved(initialImage);
    setDraft(initialImage);
  }, [initialImage]);

  // Revoke preview URLs on unmount
  useEffect(() => {
    const urls = objectUrls.current;
    return () => urls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  const isDirty = draft?.id !== saved?.id;

  const openFilePicker = () => {
    if (isViewer || isBusy) return;
    inputRef.current?.click();
  };

  // Upload the file as soon as it is selected; Save links it to the club.
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setError("");

    const validationError = validateImageFile(file, 5);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsUploading(true);
      const payload = new FormData();
      payload.append("file", file);
      const response = await callApi({
        method: "post",
        url: "file/upload",
        data: payload,
      });
      if (!response?.id) return;

      // Show the local preview so the image doesn't wait on S3
      const url = URL.createObjectURL(file);
      objectUrls.current.push(url);
      setDraft({ id: response.id, fileUrl: response.fileUrl, url });
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setDraft(null);
    setError("");
  };

  const handleSave = async () => {
    if (!isDirty) return;
    try {
      setIsSaving(true);
      const response = await callApi({
        method: "post",
        url: `club/upload-website-image/${selectedClub?.id}`,
        data: { [slot.field]: draft?.id || null },
      });
      if (response.status === 200 || response.status === 201) {
        setSaved(draft);
        toast.success(
          draft ? "Image saved successfully!" : "Image removed successfully",
          { id: `${slot.key}SaveImageToast` }
        );
      }
    } catch (err) {
      console.error("Save website image failed", err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl p-6 main-shadow">
      <div className="flex items-start justify-between gap-4 mb-4 min-h-[48px]">
        <div className="min-w-0">
          <h2 className="text-lg md:text-xl font-semibold text-black tracking-[0] leading-[normal]">
            {slot.section || slot.title}
          </h2>
          {slot.section && (
            <p className="text-sm text-textcolor mt-1">{slot.title}</p>
          )}
        </div>
        {!isViewer && (
          <button
            onClick={handleSave}
            disabled={!isDirty || isBusy}
            className="shrink-0 px-6 py-2 bg-primary hover:bg-hover disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium text-base rounded-full transition-colors"
          >
            {isSaving ? "Saving..." : isUploading ? "Uploading..." : "Save"}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <ErrorMessage message={error} />
        </div>
      )}

      {/* Hidden file input - single image only */}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
        disabled={isViewer}
      />

      <div className="relative h-[260px]">
        {isBusy && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10 rounded-xl">
            <div className="w-8 h-8 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {draft ? (
          <div className="relative h-full">
            <img
              src={draft.url}
              alt={slot.title}
              className="w-full h-full object-cover rounded-xl"
            />
            {!isViewer && (
              <>
                <button
                  onClick={openFilePicker}
                  className="cursor-pointer absolute bottom-3 left-3 inline-flex items-center gap-1 px-3 py-1 text-sm font-medium text-white bg-black/50 hover:bg-black/70 rounded transition-colors"
                  title="Change image"
                >
                  <Pencil className="w-4 h-4" />
                  Edit
                </button>
              </>
            )}
          </div>
        ) : (
          <button
            onClick={openFilePicker}
            disabled={isViewer}
            className="w-full h-full px-6 border-2 border-dashed border-[#A9B7C6] bg-[#E8EDF2] rounded-xl flex items-center justify-center transition-colors enabled:hover:border-primary enabled:cursor-pointer disabled:cursor-default"
          >
            <div className="text-center max-w-[340px]">
              {slot.optional && (
                <span className="inline-block mb-3 px-2 py-1 text-[10px] font-bold tracking-wider text-white uppercase bg-[#546476] rounded">
                  Optional — shows only if uploaded
                </span>
              )}
              <p className="text-sm font-bold tracking-widest text-[#546476] uppercase mb-2">
                {slot.title}
              </p>
              <p className="text-sm text-[#8A96A3]">{slot.description}</p>
              {!isViewer && (
                <p className="text-xs font-medium text-primary mt-3">
                  Click to select image · png, jpg, webp (Max 5MB)
                </p>
              )}
            </div>
          </button>
        )}
      </div>
    </div>
  );
};

function ImageManagemennt() {
  const { selectedClub, userRole } = useGlobalStates();
  const isViewer = userRole === UserRole.Viewer;
  const [websiteImages, setWebsiteImages] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedClub?.id) return;
    const fetchWebsiteImages = async () => {
      try {
        setLoading(true);
        const response = await callApi({
          method: "get",
          url: `club/get-website-image/${selectedClub.id}`,
        });
        const data = response?.data || response || {};
        const images = {};
        IMAGE_SLOTS.forEach(({ field }) => {
          images[field] = toImage(data[`${field}File`] || data[field]);
        });
        setWebsiteImages(images);
      } catch (error) {
        console.log("error while fetching website images", error);
      } finally {
        setLoading(false);
      }
    };
    fetchWebsiteImages();
  }, [selectedClub?.id]);

  return (
    <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-6">
      {loading && <CommonLoader fullScreen />}
      {IMAGE_SLOTS.map((slot) => (
        <ImageSlotCard
          key={slot.key}
          slot={slot}
          isViewer={isViewer}
          initialImage={websiteImages[slot.field] || null}
        />
      ))}
    </div>
  );
}

export default ImageManagemennt;
